import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faExclamationTriangle, faEdit, faEnvelope, faArrowRight, faCheckCircle, faCheck, faCopy, faUsers, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import config from '../config';
import { useNavigate } from 'react-router-dom';
import AffiliateProgramsTable from './AffiliateProgramsTable';
import NewsletterRequestForm from './NewsletterRequestForm';
import CompanyLogo from './CompanyLogo';
import NewsletterLogo from './NewsletterLogo';
import '../css/PaidSponsorTable.css';
import '../css/components/AffiliateProgramsTable.css';
import { trackSponsorCardClicked } from '../utils/funnelTracking';
import { deriveNewsletterDisplay } from '../utils/newsletterLogo';
import { normalizeTags } from '../utils/normalizeTags';

// Below this, the niche is probably underserved rather than the user
// having a typo'd search — show the "request a newsletter" nudge even
// though there are some real results, not only on a hard zero.
const LOW_RESULTS_THRESHOLD = 15;

interface NewsletterSponsored {
    newsletterName: string;
    estimatedAudience: number;
    contentTags: string[];
    dateSponsored: string;
    emailAddress?: string;
}

interface Sponsor {
    _id: string;
    sponsorName: string;
    sponsorLink: string;
    rootDomain: string;
    tags: string[];
    newsletterSponsored: string; // Legacy field for backward compatibility
    subscriberCount: number; // Legacy field
    newslettersSponsored?: NewsletterSponsored[]; // New field
    dateSponsored?: string | Date; // Most recent newsletter date
    mostRecentNewsletterDate?: string | Date; // Most recent newsletter date
    totalPlacements?: number; // Total number of newsletter placements
    avgAudienceSize?: number; // Average audience size across all newsletters
    businessContact: string;
    sponsorEmail?: string;
    contactMethod?: 'email' | 'none';
    // Detailed contact info from Gemini
    contactPersonName?: string;
    contactPersonTitle?: string;
    contactType?: 'named_person' | 'business_email' | 'generic_email' | 'not_found';
    confidence?: number;
    analysisStatus?: 'complete' | 'manual_review_required' | 'pending';
    status?: 'pending' | 'approved' | 'rejected' | 'reviewed';
    dateAdded: string;
    isViewed?: boolean;
    isApplied?: boolean;
    dateViewed?: string;
    dateApplied?: string;
    // New structure fields
    contentTags?: string[]; // Tags for this specific newsletter placement
    placementId?: string; // Unique ID for this placement
    // Affiliate program fields
    emailVerified?: boolean;
    outreachStatus?: 'contacted' | 'responded' | 'deal_closed' | null;
    outreachDate?: string | null;
    outreachAmount?: number | null;
    isAffiliateProgram?: boolean;
    affiliateSignupLink?: string;
    commissionInfo?: string;
    interestedUsers?: string[];
    __v?: number;
    // Best Matches only — attached server-side by GET /sponsors/matched
    // (server/utils/sponsorMatching.js), absent on the regular /sponsors list.
    matchScore?: number;
    matchedTags?: string[];
    matchReasons?: string[];
}

interface SponsorUpdate {
    sponsorName: string;
    sponsorLink: string;
    rootDomain?: string;
    newsletterSponsored: string;
    subscriberCount: number;
    businessContact: string;
    tags: string[];
}

interface PaidSponsorTableProps {
    onError?: (error: string) => void;
    activeFilter: string;
    audienceFilter?: string;
    isAdmin?: boolean;
    searchQuery?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    showAffiliatePrograms?: boolean;
    statusFilter?: string;
    matchedSponsors?: any[]; // Pre-matched sponsors with match scores
    showOneTime?: boolean;
    user?: {
        email: string;
        name?: string;
        newsletterInfo?: {
            name?: string;
            topic?: string;
            audience_size?: number;
            engagement_rate?: number;
            publishing_frequency?: string;
            audience_demographics?: {
                age_range?: string;
                income_range?: string;
                location?: string;
                interests?: string[];
                job_titles?: string[];
            };
        } | null;
    };
}

// A single company↔newsletter sponsorship relationship — the unit the redesigned
// grid renders one card per, instead of one card per company with placements
// hidden behind an expand toggle. A company that has sponsored N newsletters
// produces N placements, each carrying the full parent sponsor for company-side
// info (logo, contact, actions) alongside that one newsletter's own stats.
interface Placement {
    key: string;
    sponsor: Sponsor;
    newsletterName: string;
    estimatedAudience?: number;
    contentTags: string[];
    dateSponsored?: string | Date;
    emailAddress?: string;
    hasNewsletter: boolean; // false only for the rare sponsor with zero recorded placements
}

// Flattens company-level sponsor docs into one placement per newsletter
// sponsored, so "Company X sponsored Newsletter Y" is always the visible
// unit — a company with 3 placements yields 3 cards, one per newsletter.
const flattenSponsorsToPlacements = (sponsors: Sponsor[]): Placement[] => {
    const placements: Placement[] = [];
    sponsors.forEach(sponsor => {
        const newsletters = sponsor.newslettersSponsored || [];
        if (newsletters.length > 0) {
            newsletters.forEach((n, idx) => {
                placements.push({
                    key: `${sponsor._id}-${idx}`,
                    sponsor,
                    newsletterName: n.newsletterName || 'Unnamed Newsletter',
                    estimatedAudience: n.estimatedAudience,
                    contentTags: normalizeTags((n.contentTags && n.contentTags.length > 0) ? n.contentTags : sponsor.tags),
                    dateSponsored: n.dateSponsored || sponsor.dateAdded,
                    emailAddress: n.emailAddress,
                    hasNewsletter: true,
                });
            });
        } else if (sponsor.newsletterSponsored) {
            // Legacy single-newsletter shape, pre-dating the placements array.
            placements.push({
                key: `${sponsor._id}-legacy`,
                sponsor,
                newsletterName: sponsor.newsletterSponsored,
                estimatedAudience: sponsor.subscriberCount,
                contentTags: normalizeTags(sponsor.tags),
                dateSponsored: sponsor.dateSponsored || sponsor.dateAdded,
                emailAddress: undefined,
                hasNewsletter: true,
            });
        } else {
            // No recorded placement at all — keep the sponsor visible rather
            // than silently dropping it from the list.
            placements.push({
                key: `${sponsor._id}-none`,
                sponsor,
                newsletterName: '',
                estimatedAudience: undefined,
                contentTags: normalizeTags(sponsor.tags),
                dateSponsored: sponsor.dateAdded,
                emailAddress: undefined,
                hasNewsletter: false,
            });
        }
    });
    return placements;
};

// Sample sponsors for testing
const SAMPLE_SPONSORS: Sponsor[] = [
    {
        _id: '1',
        sponsorName: 'Eight Sleep',
        sponsorLink: 'https://eightsleep.com',
        rootDomain: 'eightsleep.com',
        tags: ['Technology', 'Health', 'Sleep'],
        newsletterSponsored: 'The Hustle',
        subscriberCount: 1500000,
        businessContact: 'partnerships@eightsleep.com',
        dateAdded: '2024-01-15',
        isViewed: false,
        isApplied: false
    },
    {
        _id: '2',
        sponsorName: 'ExpressVPN',
        sponsorLink: 'https://expressvpn.com',
        rootDomain: 'expressvpn.com',
        tags: ['Technology', 'Security', 'Privacy'],
        newsletterSponsored: 'TLDR Daily',
        subscriberCount: 800000,
        businessContact: 'marketing@expressvpn.com',
        dateAdded: '2024-01-20',
        isViewed: true,
        isApplied: true
    },
    {
        _id: '3',
        sponsorName: 'EmailTree',
        sponsorLink: 'https://emailtree.com',
        rootDomain: 'emailtree.com',
        tags: ['Technology', 'Productivity', 'Email'],
        newsletterSponsored: 'Morning Brew',
        subscriberCount: 2000000,
        businessContact: 'hello@emailtree.com',
        dateAdded: '2024-02-01',
        isViewed: false,
        isApplied: false
    },
    {
        _id: '4',
        sponsorName: 'Notion',
        sponsorLink: 'https://notion.so',
        rootDomain: 'notion.so',
        tags: ['Technology', 'Productivity', 'Software'],
        newsletterSponsored: 'The Hustle',
        subscriberCount: 1200000,
        businessContact: 'partnerships@notion.so',
        dateAdded: '2024-02-10',
        isViewed: true,
        isApplied: false
    },
    {
        _id: '5',
        sponsorName: 'Stripe',
        sponsorLink: 'https://stripe.com',
        rootDomain: 'stripe.com',
        tags: ['Technology', 'Finance', 'Payments'],
        newsletterSponsored: 'TLDR Daily',
        subscriberCount: 900000,
        businessContact: 'partnerships@stripe.com',
        dateAdded: '2024-02-15',
        isViewed: false,
        isApplied: false
    },
    {
        _id: '6',
        sponsorName: 'Calm',
        sponsorLink: 'https://calm.com',
        rootDomain: 'calm.com',
        tags: ['Health', 'Mental Health', 'Wellness'],
        newsletterSponsored: 'Morning Brew',
        subscriberCount: 1800000,
        businessContact: 'business@calm.com',
        dateAdded: '2024-02-20',
        isViewed: true,
        isApplied: true
    },
    {
        _id: '7',
        sponsorName: 'Figma',
        sponsorLink: 'https://figma.com',
        rootDomain: 'figma.com',
        tags: ['Technology', 'Design', 'Productivity'],
        newsletterSponsored: 'The Hustle',
        subscriberCount: 1100000,
        businessContact: 'partnerships@figma.com',
        dateAdded: '2024-02-25',
        isViewed: false,
        isApplied: false
    },
    {
        _id: '8',
        sponsorName: 'Spotify',
        sponsorLink: 'https://spotify.com',
        rootDomain: 'spotify.com',
        tags: ['Entertainment', 'Music', 'Technology'],
        newsletterSponsored: 'Morning Brew',
        subscriberCount: 2500000,
        businessContact: 'business@spotify.com',
        dateAdded: '2024-03-01',
        isViewed: true,
        isApplied: false
    },
    {
        _id: '9',
        sponsorName: 'Amazon Associates',
        sponsorLink: 'https://affiliate-program.amazon.com',
        rootDomain: 'amazon.com',
        tags: ['Affiliate', 'Ecommerce', 'Retail'],
        newsletterSponsored: 'The Hustle',
        subscriberCount: 1000000,
        businessContact: 'https://affiliate-program.amazon.com/join',
        isAffiliateProgram: true,
        affiliateSignupLink: 'https://affiliate-program.amazon.com/join',
        commissionInfo: 'Up to 10% commission on qualifying purchases',
        dateAdded: '2024-03-20',
        isViewed: false,
        isApplied: false
    },
    {
        _id: '10',
        sponsorName: 'Shopify Partners',
        sponsorLink: 'https://partners.shopify.com',
        rootDomain: 'shopify.com',
        tags: ['Affiliate', 'Ecommerce', 'Technology'],
        newsletterSponsored: 'Morning Brew',
        subscriberCount: 800000,
        businessContact: 'https://partners.shopify.com/affiliates',
        isAffiliateProgram: true,
        affiliateSignupLink: 'https://partners.shopify.com/affiliates',
        commissionInfo: 'Earn up to $2,000 per referral',
        dateAdded: '2024-03-25',
        isViewed: false,
        isApplied: false
    }
];

// A raw "26% match" reads as noise once you see it on every card — a user
// scanning ten cards that all say 24-31% concludes the matching is fake,
// even though the underlying scores are genuinely doing real work ordering
// the list. A qualitative label makes the same signal readable without
// inviting that false precision. Thresholds are tuned to the actual scoring
// formula in server/utils/sponsorMatching.js (matched-tag count * 15, +20
// topic bonus, capped at 100, then dampened 0.4-1x for audience-size fit) —
// revisit these if that formula changes.
const getMatchTier = (score: number): 'strong' | 'good' | 'recommended' => {
    if (score >= 60) return 'strong';
    if (score >= 30) return 'good';
    return 'recommended';
};

const getMatchLabel = (score: number): string => {
    const tier = getMatchTier(score);
    if (tier === 'strong') return 'Strong Match';
    if (tier === 'good') return 'Good Match';
    return 'Recommended';
};

// Contact is deliberately a secondary element now, not the card's dominant
// visual — the evidence (why this sponsor is worth contacting) and the
// pitch-generation action are the actual value; the address is just a
// detail you need once you've decided to act on that. Email-only: renders
// nothing actionable when there's no real email on record.
const formatContactDisplay = (sponsor: Sponsor, onEmailClick: (sponsor: Sponsor) => void) => {
    const rawContact = sponsor.sponsorEmail || sponsor.businessContact || '';
    const email = rawContact.includes('@') ? rawContact : null;

    if (!email) {
        return <span className="no-contact">No contact</span>;
    }

    const personName = sponsor.contactPersonName?.trim();
    const personTitle = sponsor.contactPersonTitle?.trim();

    const copyEmail = async (e: React.MouseEvent) => {
        e.stopPropagation();
        try { await navigator.clipboard.writeText(email); } catch { /* clipboard optional */ }
    };

    // No named person on record: say what the address is rather than leave
    // a bare email floating with no context.
    const subtitle = personName ? personTitle : (personTitle || 'Partnership contact');

    return (
        <div className="sponsor-contact">
            {personName && <p className="sponsor-contact__name">{personName}</p>}
            {subtitle && <p className="sponsor-contact__title">{subtitle}</p>}
            <div className="sponsor-contact__box">
                <FontAwesomeIcon
                    icon={sponsor.emailVerified ? faCheckCircle : faEnvelope}
                    className={sponsor.emailVerified ? 'sponsor-contact__verified' : 'sponsor-contact__unverified'}
                    title={sponsor.emailVerified ? 'Verified contact' : 'Contact'}
                />
                <span className="sponsor-contact__email">{email}</span>
                <button className="sponsor-contact__action" onClick={copyEmail} title="Copy email address">
                    <FontAwesomeIcon icon={faCopy} />
                </button>
                <button
                    className="sponsor-contact__action"
                    onClick={(e) => { e.stopPropagation(); onEmailClick(sponsor); }}
                    title={personName ? `Email ${personName} <${email}>` : `Email: ${email}`}
                >
                    <FontAwesomeIcon icon={faEnvelope} />
                </button>
            </div>
        </div>
    );
};

const PaidSponsorTable: React.FC<PaidSponsorTableProps> = ({ onError, activeFilter, audienceFilter = 'any', isAdmin, searchQuery, sortBy = 'dateSponsored', sortOrder = 'desc', showAffiliatePrograms = false, statusFilter = 'all', matchedSponsors, showOneTime = true, user }) => {
    const navigate = useNavigate();
    const [allSponsors, setAllSponsors] = useState<Sponsor[]>([]);
    const [displayedPlacements, setDisplayedPlacements] = useState<Placement[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [error, setError] = useState<string | null>(null);
    const ITEMS_PER_PAGE = 50;
    
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const [editingSponsor, setEditingSponsor] = useState<Sponsor | null>(null);
    const [editFormData, setEditFormData] = useState<Partial<Sponsor>>({});

    // Fetch all sponsors initially
    const fetchAllSponsors = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Check if we're in local development
            const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
            
            if (isLocalDev) {
                // Use sample sponsors for development
                setAllSponsors(SAMPLE_SPONSORS);
                setLoading(false);
                return;
            }
            
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }
            const response = await axios.get(`${config.backendUrl}sponsors`, {
                headers: {
                    'x-auth-token': token
                },
                params: {
                    affiliateOnly: showAffiliatePrograms.toString()
                }
            });
            setAllSponsors(response.data);      
        } catch (err) {
            let errorMessage = 'Something went wrong while loading sponsors.';
            
            if (axios.isAxiosError(err)) {
                if (err.response?.status === 401) {
                    errorMessage = 'Your session has expired. Please log in again.';
                    localStorage.removeItem('token');
                    navigate('/login');
                    return;
                } else if (err.response?.status === 404) {
                    errorMessage = 'Unable to find sponsor data. Please try again later.';
                } else if (err.response?.status === 403) {
                    errorMessage = 'You do not have access to view sponsors.';
                } else if (err.response?.status === 500) {
                    errorMessage = 'Server error. Our team has been notified.';
                } else if (!err.response) {
                    errorMessage = 'Network error. Please check your connection.';
                }
            } else if (err instanceof Error) {
                errorMessage = err.message;
            }
            
            setError(errorMessage);
            if (onError) onError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [navigate, onError, showAffiliatePrograms]);

    useEffect(() => {
        // If matched sponsors provided, use them directly
        if (matchedSponsors && matchedSponsors.length > 0) {
            setAllSponsors(matchedSponsors);
            setLoading(false);
        } else {
            fetchAllSponsors();
        }
        // Reset page when filters change
        setPage(1);
    }, [showAffiliatePrograms, matchedSponsors, fetchAllSponsors]);

    const filteredSponsors = useMemo(() => {
        let filtered = allSponsors;
        
        // Enforce user-facing constraints: only approved sponsors
        if (!isAdmin) {
            filtered = filtered.filter(s => s.status === 'approved');
        }
        
        // User-facing default: must have contact info unless viewing affiliate programs
        if (!isAdmin && !showAffiliatePrograms) {
            filtered = filtered.filter(sponsor => {
                const hasEmail = sponsor.sponsorEmail && sponsor.sponsorEmail.trim() !== '';
                const hasBusinessContactEmail = sponsor.businessContact && sponsor.businessContact.includes('@') && sponsor.businessContact.trim() !== '';
                return hasEmail || hasBusinessContactEmail;
            });
        }
        
        // Affiliate view: approved affiliates with a valid affiliate application link
        if (!isAdmin && showAffiliatePrograms) {
            filtered = filtered.filter(sponsor => (sponsor.isAffiliateProgram === true || (sponsor.tags && sponsor.tags.includes('Affiliate')))
                && sponsor.affiliateSignupLink && sponsor.affiliateSignupLink.trim() !== '');
        }
        
        // Apply category filter - only filter if there are active filters
        if (activeFilter && activeFilter.trim() !== '') {
            const filterTags = activeFilter.split(',').map(tag => tag.trim().toLowerCase()).filter(tag => tag !== '');
            if (filterTags.length > 0) {
                filtered = filtered.filter(sponsor =>
                    normalizeTags(sponsor.tags).some(tag =>
                        filterTags.some(filterTag => tag.toLowerCase() === filterTag)
                    )
                );
            }
        }
        
        return filtered;
    }, [allSponsors, activeFilter, showAffiliatePrograms, isAdmin]);

    // Flatten to one placement per company↔newsletter relationship — the unit
    // the redesigned grid displays, so audience/search filtering below applies
    // to the specific newsletter on each card rather than the parent company.
    const allPlacements = useMemo(() => flattenSponsorsToPlacements(filteredSponsors), [filteredSponsors]);

    const filteredPlacements = useMemo(() => {
        let filtered = allPlacements;

        // Audience-fit filter: match this placement's own newsletter audience
        if (audienceFilter && audienceFilter !== 'any') {
            const brackets: Record<string, [number, number]> = {
                'under10k': [0, 10000],
                '10k-50k': [10000, 50000],
                '50k-250k': [50000, 250000],
                '250k+': [250000, Infinity],
            };
            const range = brackets[audienceFilter];
            if (range) {
                filtered = filtered.filter(p =>
                    typeof p.estimatedAudience === 'number' &&
                    p.estimatedAudience >= range[0] && p.estimatedAudience < range[1]
                );
            }
        }

        // Apply search filter if searchQuery exists
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(p =>
                p.sponsor.sponsorName.toLowerCase().includes(query) ||
                p.newsletterName.toLowerCase().includes(query) ||
                p.contentTags.some(tag => tag.toLowerCase().includes(query))
            );
        }

        return filtered;
    }, [allPlacements, audienceFilter, searchQuery]);

    const sortedPlacements = useMemo(() => {
        return [...filteredPlacements].sort((a, b) => {
            if (sortBy === 'subscriberCount') {
                const audA = a.estimatedAudience || 0;
                const audB = b.estimatedAudience || 0;
                return sortOrder === 'asc' ? audA - audB : audB - audA;
            } else if (sortBy === 'dateAdded' || sortBy === 'dateSponsored') {
                const dateA = new Date(a.dateSponsored || 0).getTime();
                const dateB = new Date(b.dateSponsored || 0).getTime();
                return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
            } else if (sortBy === 'sponsorName') {
                return sortOrder === 'asc'
                    ? a.sponsor.sponsorName.localeCompare(b.sponsor.sponsorName)
                    : b.sponsor.sponsorName.localeCompare(a.sponsor.sponsorName);
            } else if (sortBy === 'matchScore') {
                const scoreA = (a.sponsor as any).matchScore || 0;
                const scoreB = (b.sponsor as any).matchScore || 0;
                return sortOrder === 'asc' ? scoreA - scoreB : scoreB - scoreA;
            }
            return 0;
        });
    }, [filteredPlacements, sortBy, sortOrder]);

    // Update displayed placements when filter changes or page changes
    useEffect(() => {
        const startIndex = 0;
        const endIndex = page * ITEMS_PER_PAGE;
        const newDisplayedPlacements = sortedPlacements.slice(startIndex, endIndex);
        setDisplayedPlacements(newDisplayedPlacements);
    }, [sortedPlacements, page]);

    // Calculate total pages
    const totalPages = Math.ceil(sortedPlacements.length / ITEMS_PER_PAGE);
    
    // Generate page numbers for pagination
    const getPageNumbers = () => {
        const pages: (number | string)[] = [];
        const maxVisible = 7; // Show max 7 page buttons
        
        if (totalPages <= maxVisible) {
            // Show all pages if total is less than max
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Always show first page
            pages.push(1);
            
            if (page > 3) {
                pages.push('...');
            }
            
            // Show pages around current page
            const start = Math.max(2, page - 1);
            const end = Math.min(totalPages - 1, page + 1);
            
            for (let i = start; i <= end; i++) {
                pages.push(i);
            }
            
            if (page < totalPages - 2) {
                pages.push('...');
            }
            
            // Always show last page
            pages.push(totalPages);
        }
        
        return pages;
    };

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
            // Scroll to top of table when page changes
            const tableContainer = document.querySelector('.sponsor-table-container');
            if (tableContainer) {
                tableContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    };

    const handleEdit = (sponsor: Sponsor) => {
        setEditingSponsor(sponsor);
        setEditFormData(sponsor);
    };

    const handleEditSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingSponsor) return;

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }

            const updateData: SponsorUpdate = {
                sponsorName: editFormData.sponsorName || '',
                sponsorLink: editFormData.sponsorLink || '',
                rootDomain: editFormData.rootDomain || '',
                newsletterSponsored: editFormData.newsletterSponsored || '',
                subscriberCount: editFormData.subscriberCount || 0,
                businessContact: editFormData.businessContact || '',
                tags: editFormData.tags || []
            };

            const response = await axios.put(
                `${config.backendUrl}sponsors/${editingSponsor._id}`,
                updateData,
                {
                    headers: {
                        'x-auth-token': token
                    }
                }
            );

            // Update the sponsor in the local state
            setAllSponsors(prevSponsors => 
                prevSponsors.map(sponsor => 
                    sponsor._id === editingSponsor._id ? response.data : sponsor
                )
            );

            setEditingSponsor(null);
            setEditFormData({});
        } catch (err) {
            let errorMessage = 'Something went wrong while updating the sponsor.';
            if (axios.isAxiosError(err)) {
                errorMessage = err.response?.data || errorMessage;
            }
            setError(errorMessage);
            if (onError) onError(errorMessage);
        }
    };

    const handleEditCancel = () => {
        setEditingSponsor(null);
        setEditFormData({});
    };

    const [generatingPitchFor, setGeneratingPitchFor] = useState<string | null>(null);

    const handleGeneratePitch = async (sponsor: Sponsor) => {
        const token = localStorage.getItem('token');
        if (!token || generatingPitchFor) return;
        setGeneratingPitchFor(sponsor._id);
        try {
            const response = await axios.post(
                `${config.backendUrl}sponsors/${sponsor._id}/generate-pitch`,
                {},
                { headers: { 'x-auth-token': token }, timeout: 45000 }
            );
            const { subject, body: draftBody } = response.data;
            // Same address-priority order as the standard template (handleEmailClick)
            // — was businessContact-first here, sponsorEmail-first there, so the two
            // outreach paths could silently send to different addresses for the
            // same sponsor.
            const email = sponsor.sponsorEmail?.includes('@') ? sponsor.sponsorEmail : sponsor.businessContact;
            // The prompt deliberately omits a sign-off name ("the sender adds
            // their own") — this is that step. Real name when we have one,
            // otherwise just the newsletter name; never a placeholder.
            const senderName = user?.name?.trim();
            const newsletterName = user?.newsletterInfo?.name || 'My Newsletter';
            const signOff = senderName ? `${senderName}\n${newsletterName}` : newsletterName;
            const body = `${draftBody}\n\nBest,\n${signOff}`;
            try { await navigator.clipboard.writeText(`Subject: ${subject}

${body}`); } catch { /* clipboard optional */ }
            window.location.href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
            markAsApplied(sponsor._id);
        } catch (err) {
            console.error('Pitch generation failed', err);
            onError?.('Pitch generation failed — try again in a moment.');
        } finally {
            setGeneratingPitchFor(null);
        }
    };

    // The primary action on every card — not "AI Pitch" (nobody cares that
    // it's AI-generated; they care about going from "this looks relevant"
    // to "I have a good email ready to send"). Outreach status tracking
    // (contacted/replied/won) lives on the Analytics page's pipeline board,
    // not here — this button only needs to get a draft in front of someone.
    const renderOutreachControls = (sponsor: Sponsor) => (
        <div className="outreach-controls" onClick={(e) => e.stopPropagation()}>
            <button
                className="pitch-btn"
                disabled={generatingPitchFor === sponsor._id}
                onClick={(e) => { e.stopPropagation(); handleGeneratePitch(sponsor); }}
                title="Draft a personalized pitch email"
            >
                <FontAwesomeIcon icon={faWandMagicSparkles} />
                {generatingPitchFor === sponsor._id ? 'Writing…' : 'Generate personalized pitch'}
                {generatingPitchFor !== sponsor._id && <FontAwesomeIcon icon={faArrowRight} />}
            </button>
        </div>
    );

    const markAsViewed = async (sponsorId: string) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }

            const response = await axios.post(
                `${config.backendUrl}sponsors/${sponsorId}/view`,
                {},
                {
                    headers: {
                        'x-auth-token': token
                    }
                }
            );

            // Update the sponsor in the local state using the server response
            const updatedSponsor = response.data;
            setAllSponsors(prevSponsors => 
                prevSponsors.map(sponsor => 
                    sponsor._id === sponsorId ? { 
                        ...sponsor, 
                        isViewed: updatedSponsor.isViewed,
                        dateViewed: updatedSponsor.dateViewed
                    } : sponsor
                )
            );
        } catch (err) {
            console.error('Error marking sponsor as viewed:', err);
        }
    };

    const markAsApplied = async (sponsorId: string) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }

            const response = await axios.post(
                `${config.backendUrl}sponsors/${sponsorId}/apply`,
                {},
                {
                    headers: {
                        'x-auth-token': token
                    }
                }
            );


            // Update the sponsor in the local state using the server response
            const updatedSponsor = response.data;
            setAllSponsors(prevSponsors => 
                prevSponsors.map(sponsor => 
                    sponsor._id === sponsorId ? { 
                        ...sponsor, 
                        isApplied: updatedSponsor.isApplied, 
                        isViewed: updatedSponsor.isViewed,
                        dateApplied: updatedSponsor.dateApplied,
                        dateViewed: updatedSponsor.dateViewed
                    } : sponsor
                )
            );
        } catch (err) {
            console.error('Client: Error marking sponsor as applied:', err);
            if (axios.isAxiosError(err)) {
                console.error('Client: Response data:', err.response?.data);
                console.error('Client: Response status:', err.response?.status);
            }
        }
    };

    const handleSponsorClick = (sponsor: Sponsor) => {
        // Track sponsor card clicked (funnel tracking)
        trackSponsorCardClicked(sponsor.sponsorName || sponsor.rootDomain || 'unknown');
        
        if (!sponsor.isViewed) {
            markAsViewed(sponsor._id);
        }
        // Open sponsor website
        window.open(`https://${sponsor.rootDomain}`, '_blank');
    };


    // Concise, human-sounding, and grounded only in data that actually
    // exists — no filler like "a growing community of engaged readers" when
    // there's no audience number to back it up, and no "[Your Name]"
    // placeholder (signs with the real account name when we have one,
    // otherwise just the newsletter name — never a bracketed stand-in).
    const generateEmailTemplate = (sponsor: Sponsor) => {
        const info = user?.newsletterInfo;
        const newsletterName = info?.name || 'my newsletter';
        const newsletterTopic = info?.topic || '';
        const audienceSize = info?.audience_size || 0;
        const engagementRate = info?.engagement_rate || 0;
        const publishingFreq = info?.publishing_frequency || '';
        const demographics = info?.audience_demographics;
        const senderName = user?.name?.trim();

        const firstName = sponsor.contactPersonName?.trim().split(/\s+/)[0];
        const greeting = firstName ? `Hi ${firstName},` : 'Hi there,';

        // Reference a specific past placement when we have one — more
        // credible than a generic "sponsors newsletters in this space" line.
        const mostRecentPlacement = [...(sponsor.newslettersSponsored || [])]
            .sort((a, b) => new Date(b.dateSponsored || 0).getTime() - new Date(a.dateSponsored || 0).getTime())[0];

        const intro = `I run ${newsletterName}${newsletterTopic ? `, a newsletter about ${newsletterTopic}` : ''}.`;

        const hook = mostRecentPlacement
            ? `I saw you sponsored ${mostRecentPlacement.newsletterName} — thought ${newsletterTopic ? `a ${newsletterTopic} audience` : 'my readers'} could be a good fit too.`
            : `I think ${sponsor.sponsorName} and ${newsletterTopic ? `a ${newsletterTopic} audience` : 'my readers'} could be a good fit.`;

        const statLines: string[] = [];
        if (audienceSize > 0) statLines.push(`${audienceSize.toLocaleString()} subscribers`);
        if (engagementRate > 0) statLines.push(`${engagementRate}% engagement rate`);
        if (publishingFreq) statLines.push(`published ${publishingFreq}`);
        if (demographics) {
            const parts = [];
            if (demographics.age_range) parts.push(`ages ${demographics.age_range}`);
            if (demographics.income_range) parts.push(`${demographics.income_range} income`);
            if (demographics.location) parts.push(demographics.location);
            if (parts.length > 0) statLines.push(`audience mostly ${parts.join(', ')}`);
        }
        const stats = statLines.length > 0 ? statLines.map(s => `- ${s}`).join('\n') : '';

        const ask = 'Are you open to discussing a sponsorship?';
        const signOff = senderName ? `${senderName}\n${newsletterName}` : newsletterName;

        const subject = `Sponsorship — ${newsletterName}`;
        const body = [greeting, intro, hook, stats, ask, `Best,\n${signOff}`]
            .filter(Boolean)
            .join('\n\n');

        return {
            subject: encodeURIComponent(subject),
            body: encodeURIComponent(body)
        };
    };

    const handleEmailClick = (sponsor: Sponsor) => {
        // Email-only outreach: prefer the dedicated sponsorEmail field, fall back to a
        // legacy businessContact value only if it actually looks like an email address.
        const rawContact = sponsor.sponsorEmail || sponsor.businessContact || '';
        const email = rawContact.includes('@') ? rawContact : null;

        if (!email) {
            return;
        }

        const template = generateEmailTemplate(sponsor);
        const mailtoLink = `mailto:${email}?subject=${template.subject}&body=${template.body}`;
        
        // Mark as applied and viewed
        if (!sponsor.isApplied) {
            markAsApplied(sponsor._id);
        }
        if (!sponsor.isViewed) {
            markAsViewed(sponsor._id);
        }
        
        // Open email client
        window.location.href = mailtoLink;
    };

    // One card per company↔newsletter placement — company info on the left,
    // the specific sponsored newsletter's own stats on the right, so "Company X
    // sponsored Newsletter Y" is legible at a glance while scrolling, with
    // nothing hidden behind an expand click. Shared between the mobile and
    // desktop layouts; CSS handles the side-by-side vs. stacked arrangement.
    const renderPlacementCard = (placement: Placement) => {
        const { sponsor } = placement;
        const matchScore = sponsor.matchScore;
        // Some stored newsletterName values are actually the raw sending
        // email (no display name on the source email's From header) — this
        // derives a presentable name + logo domain either way instead of
        // ever rendering an email address as if it were a name.
        const newsletterDisplay = deriveNewsletterDisplay(placement.newsletterName, placement.emailAddress);
        const sponsorTags = normalizeTags(sponsor.tags);

        const audienceLabel = placement.estimatedAudience
            ? (placement.estimatedAudience >= 1000
                ? `${Math.round(placement.estimatedAudience / 1000).toLocaleString()}K`
                : `${placement.estimatedAudience}`)
            : null;
        const reasons = matchScore !== undefined ? (sponsor.matchReasons || []) : [];

        return (
            <div
                key={placement.key}
                className={[
                    'placement-card',
                    (sponsor.isViewed || sponsor.isApplied) ? 'placement-card--inactive' : '',
                ].filter(Boolean).join(' ')}
            >
                <div className="placement-card__header">
                    <CompanyLogo domain={sponsor.rootDomain} name={sponsor.sponsorName} size={36} />
                    <a
                        href={`https://${sponsor.rootDomain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="placement-card__name"
                        onClick={() => handleSponsorClick(sponsor)}
                    >
                        {sponsor.sponsorName}
                    </a>
                    {matchScore !== undefined && (
                        <span className={`placement-card__match placement-card__match--${getMatchTier(matchScore)}`}>
                            {getMatchLabel(matchScore)}
                        </span>
                    )}
                </div>

                {sponsorTags.length > 0 && (
                    <div className="placement-card__tags">
                        {sponsorTags.slice(0, 3).map((tag, idx) => (
                            <span key={idx} className="placement-card__tag">{tag}</span>
                        ))}
                    </div>
                )}

                {reasons.length > 0 && (
                    <ul className="placement-card__reasons">
                        {reasons.map((reason, idx) => (
                            <li key={idx}>
                                <FontAwesomeIcon icon={faCheck} />
                                {reason}
                            </li>
                        ))}
                    </ul>
                )}

                {formatContactDisplay(sponsor, handleEmailClick)}

                <div className="placement-card__sponsorship">
                    {placement.hasNewsletter ? (
                        <>
                            <div className="placement-card__sponsorship-row">
                                <FontAwesomeIcon icon={faArrowRight} className="placement-card__sponsorship-icon" />
                                <NewsletterLogo domain={newsletterDisplay.domain} size={20} />
                                <span className="placement-card__newsletter-name">{newsletterDisplay.name}</span>
                                {audienceLabel && (
                                    <span className="placement-card__audience">
                                        <FontAwesomeIcon icon={faUsers} /> {audienceLabel}
                                    </span>
                                )}
                            </div>
                            {placement.dateSponsored && (
                                <div className="placement-card__date">
                                    Sponsored {new Date(placement.dateSponsored).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="placement-card__date">No newsletter placement recorded yet</div>
                    )}
                </div>

                {renderOutreachControls(sponsor)}
            </div>
        );
    };

    if (error) {
        return (
            <div className="sponsor-table__error">
                <FontAwesomeIcon icon={faExclamationTriangle} className="sponsor-table__error-icon" />
                <p>{error}</p>
                <p className="sponsor-table__error-subtext">
                    If this issue persists, please contact support.
                </p>
                <button 
                    className="sponsor-table__error-retry"
                    onClick={() => fetchAllSponsors()}
                >
                    Try Again
                </button>
            </div>
        );
    }

    if (loading && allSponsors.length === 0) {
        return (
            <div className="sponsor-table__loading-container">
                <FontAwesomeIcon icon={faSpinner} spin className="sponsor-table__loading-icon" />
                <p>Loading sponsors...</p>
            </div>
        );
    }

    // Show affiliate programs table if toggle is on (check BEFORE mobile/desktop view)
    if (showAffiliatePrograms) {
        return (
            <AffiliateProgramsTable 
                onError={onError || (() => {})} 
                user={user}
                isMobile={isMobile}
            />
        );
    }

    // Mobile view - one placement card per company↔newsletter pairing (full scrollable list, no pagination)
    if (isMobile) {
        return (
            <div className="placements-grid placements-grid--mobile">
                {sortedPlacements.length > 0 ? (
                    <>
                    {sortedPlacements.map((placement) => renderPlacementCard(placement))}
                    {sortedPlacements.length < LOW_RESULTS_THRESHOLD && (
                        <div className="low-results-banner">
                            <span className="no-results__cta-label">Know another newsletter in this niche? Suggest it and we'll start tracking its sponsors for you.</span>
                            <NewsletterRequestForm prefillNiche={searchQuery || activeFilter} />
                        </div>
                    )}
                    </>
                ) : (
                    <div className="mobile-no-results">
                        <p>No sponsors found. Try adjusting your filters or search terms.</p>
                        <div className="no-results__cta-row">
                            <span className="no-results__cta-label">Know another newsletter in this niche? Suggest it and we'll start tracking its sponsors for you.</span>
                            <NewsletterRequestForm prefillNiche={searchQuery || activeFilter} />
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // Desktop view - one placement card per company↔newsletter pairing, paginated
    return (
        <>
        <div className="sponsor-table-container">
            <div className="sponsor-table-optimized">
                {!loading && displayedPlacements.length === 0 && (
                    <div className="sponsor-table__empty-cell">
                        <p>No sponsors found. Try adjusting your filters or search terms.</p>
                        <div className="no-results__cta-row">
                            <span className="no-results__cta-label">Know another newsletter in this niche? Suggest it and we'll start tracking its sponsors for you.</span>
                            <NewsletterRequestForm prefillNiche={searchQuery || activeFilter} />
                        </div>
                    </div>
                )}

                <div className="placements-grid placements-grid--desktop">
                    {displayedPlacements.map((placement) => renderPlacementCard(placement))}
                </div>

                {!loading && sortedPlacements.length > 0 && sortedPlacements.length < LOW_RESULTS_THRESHOLD && (
                    <div className="low-results-banner">
                        <span className="no-results__cta-label">Know another newsletter in this niche? Suggest it and we'll start tracking its sponsors for you.</span>
                        <NewsletterRequestForm prefillNiche={searchQuery || activeFilter} />
                    </div>
                )}

                {loading && allSponsors.length > 0 && (
                    <div className="sponsor-table__loading">
                        <FontAwesomeIcon icon={faSpinner} spin className="sponsor-table__loading-icon" />
                        Loading sponsors...
                    </div>
                )}
                
                {/* Pagination Controls */}
                {!loading && totalPages > 1 && (
                    <div className="sponsor-table__pagination">
                        <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(page - 1)}
                            disabled={page === 1}
                            aria-label="Previous page"
                        >
                            Previous
                        </button>
                        
                        <div className="pagination-pages">
                            {getPageNumbers().map((pageNum, idx) => {
                                if (pageNum === '...') {
                                    return <span key={`ellipsis-${idx}`} className="pagination-ellipsis">...</span>;
                                }
                                return (
                                    <button
                                        key={pageNum}
                                        className={`pagination-page ${page === pageNum ? 'active' : ''}`}
                                        onClick={() => handlePageChange(pageNum as number)}
                                        aria-label={`Go to page ${pageNum}`}
                                        aria-current={page === pageNum ? 'page' : undefined}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                        </div>
                        
                        <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(page + 1)}
                            disabled={page === totalPages}
                            aria-label="Next page"
                        >
                            Next
                        </button>
                    </div>
                )}
                
                {!loading && displayedPlacements.length > 0 && (
                    <div className="sponsor-table__pagination-info">
                        Showing {displayedPlacements.length} of {sortedPlacements.length} sponsorships
                        {totalPages > 1 && ` (Page ${page} of ${totalPages})`}
                    </div>
                )}
            </div>
        </div>

        {editingSponsor && (
            <div className="sponsor-table__edit-modal">
                <div className="sponsor-table__edit-form">
                    <h3>Edit Sponsor</h3>
                    <form onSubmit={handleEditSubmit}>
                        <div className="sponsor-table__form-group">
                            <label>Sponsor Name</label>
                            <input
                                type="text"
                                value={editFormData.sponsorName || ''}
                                onChange={(e) => setEditFormData({...editFormData, sponsorName: e.target.value})}
                                required
                            />
                        </div>
                        <div className="sponsor-table__form-group">
                            <label>Sponsor Link</label>
                            <input
                                type="url"
                                value={editFormData.sponsorLink || ''}
                                onChange={(e) => setEditFormData({...editFormData, sponsorLink: e.target.value})}
                            />
                        </div>
                        <div className="sponsor-table__form-group">
                            <label>Root Domain</label>
                            <input
                                type="text"
                                value={editFormData.rootDomain || ''}
                                onChange={(e) => setEditFormData({...editFormData, rootDomain: e.target.value})}
                            />
                        </div>
                        <div className="sponsor-table__form-group">
                            <label>Newsletter Sponsored</label>
                            <input
                                type="text"
                                value={editFormData.newsletterSponsored || ''}
                                onChange={(e) => setEditFormData({...editFormData, newsletterSponsored: e.target.value})}
                            />
                        </div>
                        <div className="sponsor-table__form-group">
                            <label>Subscriber Count</label>
                            <input
                                type="number"
                                value={editFormData.subscriberCount || ''}
                                onChange={(e) => setEditFormData({...editFormData, subscriberCount: parseInt(e.target.value) || 0})}
                            />
                        </div>
                        <div className="sponsor-table__form-group">
                            <label>Business Contact</label>
                            <input
                                type="text"
                                value={editFormData.businessContact || ''}
                                onChange={(e) => setEditFormData({...editFormData, businessContact: e.target.value})}
                            />
                        </div>
                        <div className="sponsor-table__form-group">
                            <label>Tags (comma-separated)</label>
                            <input
                                type="text"
                                value={editFormData.tags?.join(', ') || ''}
                                onChange={(e) => setEditFormData({...editFormData, tags: e.target.value.split(',').map(tag => tag.trim())})}
                            />
                        </div>
                        <div className="sponsor-table__form-actions">
                            <button type="submit" className="sponsor-table__submit-btn">Save Changes</button>
                            <button type="button" className="sponsor-table__cancel-btn" onClick={handleEditCancel}>Cancel</button>
                        </div>
                    </form>
                </div>
            </div>
        )}
        </>
    );
};

export default PaidSponsorTable; 